import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { IconButton } from '@mui/material';
import { CaretLeft, CaretRight, FilePdf } from '@phosphor-icons/react';
import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Text } from 'components/text/Text';
import { colors } from 'styles/theme/colors';
import { isPdfFile } from '../utils';
import {
  FilePreviewRoot,
  ImgPreviewClip,
  NonPdfFallback,
  OfWord,
  PageNavFraction,
  PageNumberField,
  PdfCanvasClip,
  PreviewCanvas,
  PreviewImg,
  PreviewToolbarFloating,
  TotalPagesLabel,
} from './filePreview.styles';
import { FilePreviewPdfScrollSync } from './FilePreviewPdfScrollSync';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export type PdfPagerInfo = { currentPage: number; totalPages: number };

export type FilePreviewProps = {
  file: File;
  previewUrl: string;
  hideNavigationShowScrollSync?: boolean;
  onPdfPagerInfo?: (info: PdfPagerInfo) => void;
};

const FilePreviewInner = ({
  file,
  previewUrl,
  hideNavigationShowScrollSync = false,
  onPdfPagerInfo,
}: FilePreviewProps) => {
  const activeIsPdf = isPdfFile(file);
  const onPdfPagerInfoRef = useRef(onPdfPagerInfo);
  onPdfPagerInfoRef.current = onPdfPagerInfo;

  const [pdfPage, setPdfPage] = useState(1);
  const [pdfTotalPages, setPdfTotalPages] = useState<number | null>(null);
  const pdfRef = useRef<PDFDocumentProxy | null>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    if (!activeIsPdf) return;
    const el = pdfContainerRef.current;
    if (!el) return;

    const updateSize = () => setContainerWidth(el.clientWidth);

    updateSize();
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(updateSize);
    });

    const ro = new ResizeObserver(updateSize);
    ro.observe(el);
    window.addEventListener('resize', updateSize);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, [previewUrl, activeIsPdf]);

  useEffect(() => {
    if (!activeIsPdf) {
      void pdfRef.current?.destroy();
      pdfRef.current = null;
      setPdfPage(1);
      setPdfTotalPages(null);
      return;
    }

    setPdfPage(1);
    setPdfTotalPages(null);
    let cancelled = false;

    void (async () => {
      try {
        const raw = await file.arrayBuffer();
        if (cancelled) return;
        const data = new Uint8Array(raw);
        void pdfRef.current?.destroy();
        pdfRef.current = null;
        const pdf = await pdfjs.getDocument({ data }).promise;
        if (cancelled) {
          await pdf.destroy();
          return;
        }
        pdfRef.current = pdf;
        setPdfTotalPages(pdf.numPages);
      } catch {
        if (!cancelled) setPdfTotalPages(null);
      }
    })();

    return () => {
      cancelled = true;
      void pdfRef.current?.destroy();
      pdfRef.current = null;
    };
  }, [file, activeIsPdf]);

  useEffect(() => {
    if (pdfTotalPages != null && pdfPage > pdfTotalPages) setPdfPage(pdfTotalPages);
  }, [pdfTotalPages, pdfPage]);

  useEffect(() => {
    if (!activeIsPdf || pdfTotalPages == null) return;
    onPdfPagerInfoRef.current?.({ currentPage: pdfPage, totalPages: pdfTotalPages });
  }, [activeIsPdf, pdfPage, pdfTotalPages]);

  useEffect(() => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas || pdfTotalPages == null || !activeIsPdf || containerWidth <= 0) return;

    const maxW = Math.max(1, containerWidth);
    let cancelled = false;
    const renderTaskRef: { current: { cancel: () => void; promise: Promise<void> } | null } = {
      current: null,
    };

    void (async () => {
      try {
        const page = await pdf.getPage(Math.min(Math.max(1, pdfPage), pdf.numPages));
        if (cancelled) return;

        const base = page.getViewport({ scale: 1 });
        const scale = maxW / base.width;
        const viewport = page.getViewport({ scale });
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const w = Math.floor(viewport.width);
        const h = Math.floor(viewport.height);
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const task = page.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
        });
        renderTaskRef.current = task;
        await task.promise;
        if (!cancelled) renderTaskRef.current = null;
      } catch {
        /* cancelled */
      }
    })();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
    };
  }, [pdfPage, pdfTotalPages, containerWidth, activeIsPdf, file]);

  const goPrevToolbar = () => {
    if (activeIsPdf) setPdfPage(p => Math.max(1, p - 1));
  };

  const goNextToolbar = () => {
    if (activeIsPdf) {
      setPdfPage(p => (pdfTotalPages != null ? Math.min(pdfTotalPages, p + 1) : p + 1));
    }
  };

  const toolbarPagerDisabledPrev = activeIsPdf ? pdfPage <= 1 : true;
  const toolbarPagerDisabledNext = activeIsPdf
    ? pdfTotalPages != null && pdfPage >= pdfTotalPages
    : true;

  const currentNavNumber = activeIsPdf ? pdfPage : 1;
  const totalPagesLabel =
    activeIsPdf && pdfTotalPages != null ? String(pdfTotalPages) : activeIsPdf ? '…' : '1';

  const [pageFieldDraft, setPageFieldDraft] = useState<string | null>(null);
  const pageFieldDisplay = pageFieldDraft ?? String(currentNavNumber);

  useEffect(() => setPageFieldDraft(null), [previewUrl]);

  const applyPageFromInput = (raw: string) => {
    if (!activeIsPdf) return;
    const digits = raw.replace(/\D/g, '');
    const n = parseInt(digits, 10);
    if (Number.isNaN(n) || n < 1) return;
    const max = pdfTotalPages ?? 9999;
    setPdfPage(Math.min(max, Math.max(1, n)));
  };

  return (
    <FilePreviewRoot>
      {activeIsPdf ? (
        <PdfCanvasClip ref={pdfContainerRef}>
          <PreviewCanvas ref={canvasRef} data-pdf-page={pdfPage} aria-label={file.name} />
        </PdfCanvasClip>
      ) : file.type.startsWith('image/') ? (
        <ImgPreviewClip>
          <PreviewImg src={previewUrl} alt="" />
        </ImgPreviewClip>
      ) : (
        <NonPdfFallback>
          <FilePdf size={48} weight="duotone" color={colors.brand[600]} />
          <Text variant="bodySm" color="secondary" top={8} align="center">
            <Trans>Preview not available</Trans>
          </Text>
        </NonPdfFallback>
      )}
      {!hideNavigationShowScrollSync && (
        <PreviewToolbarFloating>
          <IconButton
            size="sm"
            aria-label={activeIsPdf ? t`Previous page` : t`Previous`}
            onClick={goPrevToolbar}
            disabled={toolbarPagerDisabledPrev}
            sx={{
              color: toolbarPagerDisabledPrev ? colors.grey[600] : colors.grey[0],
              '&.Mui-disabled': { opacity: 1 },
            }}
          >
            <CaretLeft
              size={18}
              weight="bold"
              color={toolbarPagerDisabledPrev ? colors.grey[600] : colors.grey[0]}
            />
          </IconButton>
          <PageNavFraction
            aria-label={
              activeIsPdf
                ? pdfTotalPages != null
                  ? t`Page ${pdfPage} of ${pdfTotalPages}`
                  : t`Page ${pdfPage}, total not shown`
                : t`Page 1 of 1`
            }
          >
            <PageNumberField
              type="text"
              inputMode="numeric"
              aria-label={activeIsPdf ? t`Current page` : t`Page`}
              value={pageFieldDisplay}
              onFocus={() => setPageFieldDraft(String(currentNavNumber))}
              onChange={e => setPageFieldDraft(e.target.value)}
              onBlur={e => {
                applyPageFromInput(e.target.value);
                setPageFieldDraft(null);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
              disabled={!activeIsPdf}
            />
            <OfWord>{t`of`}</OfWord>
            <TotalPagesLabel variant="bodySm">{totalPagesLabel}</TotalPagesLabel>
          </PageNavFraction>
          <IconButton
            size="sm"
            aria-label={activeIsPdf ? t`Next page` : t`Next`}
            onClick={goNextToolbar}
            disabled={toolbarPagerDisabledNext}
            sx={{
              color: toolbarPagerDisabledNext ? colors.grey[600] : colors.grey[0],
              '&.Mui-disabled': { opacity: 1 },
            }}
          >
            <CaretRight
              size={18}
              weight="bold"
              color={toolbarPagerDisabledNext ? colors.grey[600] : colors.grey[0]}
            />
          </IconButton>
        </PreviewToolbarFloating>
      )}
    </FilePreviewRoot>
  );
};

export const FilePreview = (props: FilePreviewProps) => {
  const { hideNavigationShowScrollSync, file, onPdfPagerInfo } = props;
  if (hideNavigationShowScrollSync && isPdfFile(file)) {
    return (
      <FilePreviewRoot>
        <FilePreviewPdfScrollSync file={file} onPagerInfo={onPdfPagerInfo} />
      </FilePreviewRoot>
    );
  }
  return <FilePreviewInner {...props} />;
};
